import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';

import {
  ClaimFromDispensePayload,
  DispenseVoucherQuote,
  PcseClaim,
} from '../../@types/pcse';
import { DispenseService } from '../../api/dispense.service';
import { pcseService } from '../../api/pcse.service';
import { UsersService } from '../../api/users.service';
import { normalisePcseError } from '../../hooks/usePCSEClaims';
import Iconify from '../iconify';
import GosClaimPrintDialog from './GosClaimPrintDialog';
import SignaturePad from './SignaturePad';
import { formatLabel } from '../../utils/formatLabel';
import { toUpperText, uppercaseElementValue } from '../../utils/uppercase';

const dispenseService = new DispenseService();
const usersService = new UsersService();

const money = (n?: number) => `£${(Number(n) || 0).toFixed(2)}`;

interface ClaimForDispenseDialogProps {
  open: boolean;
  onClose: () => void;
  dispenseId: number;
  /** Default name on the patient signature pad, when one has to be captured. */
  patientName?: string;
  onClaimed?: (claim: PcseClaim) => void;
}

/**
 * Raise the GOS3 claim that recovers a dispense's NHS voucher.
 *
 * The screen exists to be *short*. Everything the practice already recorded —
 * band, supplements, price, prescription, eligibility, and crucially the
 * patient's signature from handover — is read from the dispense server-side, so
 * the only thing normally asked for here is the performer's GOC number.
 *
 * When the dispense already carries a patient signature, no signature pad is
 * shown at all: the patient signed for this supply once and is not asked again.
 */
export default function ClaimForDispenseDialog({
  open,
  onClose,
  dispenseId,
  patientName,
  onClaimed,
}: ClaimForDispenseDialogProps) {
  const { enqueueSnackbar } = useSnackbar();

  const [quote, setQuote] = useState<DispenseVoucherQuote | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [gocNumber, setGocNumber] = useState('');
  /** True once the GOC number has been pre-filled from the user's profile. */
  const [gocFromProfile, setGocFromProfile] = useState(false);
  const [draft, setDraft] = useState(false);
  const [allowDuplicate, setAllowDuplicate] = useState(false);

  // Only used when the dispense carries no signature yet.
  const [signature, setSignature] = useState('');
  const [signatoryName, setSignatoryName] = useState(
    toUpperText(patientName ?? ''),
  );
  const [savingSignature, setSavingSignature] = useState(false);

  const [claim, setClaim] = useState<PcseClaim | null>(null);
  const [printOpen, setPrintOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await pcseService.getDispenseVoucher(dispenseId);
      setQuote(res.data);
    } catch (err) {
      setError(normalisePcseError(err).message);
      setQuote(null);
    } finally {
      setLoading(false);
    }
  }, [dispenseId]);

  useEffect(() => {
    if (!open) return;
    setClaim(null);
    setAllowDuplicate(false);
    load();
  }, [open, load]);

  /**
   * Pre-fill the performer number from the signed-in user's profile.
   *
   * The server defaults it too, so the claim would succeed either way — but a
   * blank required field on screen reads as "you must supply this", and the
   * whole point is that the clinician does not have to.
   */
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    usersService
      .getMySignature()
      .then((res) => {
        if (cancelled || !res.data.gocNumber) return;
        setGocNumber(toUpperText(res.data.gocNumber));
        setGocFromProfile(true);
      })
      .catch(() => {
        // A profile we cannot read is not a reason to block the claim — the
        // field simply stays empty and is asked for below.
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    setSignatoryName(toUpperText(patientName ?? ''));
  }, [patientName]);

  const alreadySigned = Boolean(quote?.patientSignature?.present);
  const existingClaim = quote?.existingClaim ?? null;

  /**
   * Capture the missing patient signature onto the DISPENSE rather than onto
   * the claim payload. Storing it where the supply lives means a resubmission,
   * a second claim or a reprint all find it — and the patient still only ever
   * signs once.
   */
  const saveSignature = async () => {
    if (!signature || !signatoryName.trim()) return;
    setSavingSignature(true);
    try {
      await dispenseService.capturePatientSignature(dispenseId, {
        image: signature,
        name: signatoryName.trim(),
        signatory: 'patient',
      });
      enqueueSnackbar('Patient signature saved against the dispense.', {
        variant: 'success',
      });
      setSignature('');
      await load();
    } catch (err) {
      enqueueSnackbar(normalisePcseError(err).message, { variant: 'error' });
    } finally {
      setSavingSignature(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const payload: ClaimFromDispensePayload = {
        prescriberGocNumber: gocNumber.trim() || undefined,
        draft,
        allowDuplicate,
        // No signatures are sent: the patient's comes from the dispense and the
        // performer/supplier from the submitting user's stored signature, both
        // filled in server-side.
      };
      const res = await pcseService.claimForDispense(dispenseId, payload);
      setClaim(res.data);
      enqueueSnackbar(
        `Claim ${res.data.id.slice(0, 8)} ${draft ? 'saved as draft' : 'submitted'} (${res.data.status})`,
        {
          variant:
            res.data.status === 'rejected' || res.data.status === 'error'
              ? 'warning'
              : 'success',
        },
      );
      onClaimed?.(res.data);
    } catch (err) {
      setError(normalisePcseError(err).message);
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * PCSE refuses a GOS3 without `Performer-List-Number`, and the backend blocks
   * it before spending a round-trip. Enforcing it here too means the failure is
   * a disabled button rather than an errored claim row the user has to
   * understand. Drafts are exempt — they never reach PCSE.
   */
  const gocMissing = !draft && !gocNumber.trim();

  const canSubmit =
    !loading &&
    !submitting &&
    Boolean(quote) &&
    !gocMissing &&
    (alreadySigned || draft);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Claim for this dispense</DialogTitle>
        <DialogContent dividers>
          {loading && (
            <Stack alignItems="center" py={4}>
              <CircularProgress />
            </Stack>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* ── Outcome ─────────────────────────────────────────── */}
          {claim && (
            <Alert
              severity={
                claim.status === 'rejected' || claim.status === 'error'
                  ? 'warning'
                  : 'success'
              }
              sx={{ mb: 2 }}
            >
              <AlertTitle>
                Claim {formatLabel(claim.status).toLowerCase()}
              </AlertTitle>
              {claim.pcseClaimReference && (
                <>
                  PCSE reference <strong>{claim.pcseClaimReference}</strong>
                  <br />
                </>
              )}
              {claim.adjudicationDetail}
              <Box mt={1}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Iconify icon="eva:printer-outline" />}
                  onClick={() => setPrintOpen(true)}
                >
                  Print NHS {claim.claimType} form
                </Button>
              </Box>
            </Alert>
          )}

          {quote && !claim && (
            <Stack spacing={2}>
              {/* ── What is being claimed ───────────────────────── */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  What will be claimed
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={`Band ${quote.band.type}`} color="primary" />
                  <Chip
                    variant="outlined"
                    label={`Charged ${money(quote.chargedAmount)}`}
                  />
                  <Chip
                    color="info"
                    label={`NHS pays ${money(quote.redeemedValue)}`}
                  />
                  <Chip
                    variant="outlined"
                    label={`Patient paid ${money(quote.patientContribution)}`}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Supplied {quote.suppliedDate}. Band, supplements and price are
                  taken from the dispense, so the amount claimed is the amount
                  discounted at the till.
                </Typography>
              </Box>

              {!quote.prescriptionFound && (
                <Alert severity="warning">
                  No refraction was found for this patient, so the claim will
                  state plano powers in its prescription block. Check the band
                  before submitting.
                </Alert>
              )}

              {existingClaim && (
                <Alert severity="warning">
                  <AlertTitle>
                    This dispense has already been claimed
                  </AlertTitle>
                  Claim {existingClaim.id.slice(0, 8)} is{' '}
                  <strong>{formatLabel(existingClaim.status)}</strong>. A
                  duplicate GOS3 is a compliance issue — resubmit that claim
                  instead unless you are certain a second is correct.
                  <FormControlLabel
                    sx={{ display: 'block', mt: 1 }}
                    control={
                      <Switch
                        checked={allowDuplicate}
                        onChange={(e) => setAllowDuplicate(e.target.checked)}
                      />
                    }
                    label="Claim again anyway"
                  />
                </Alert>
              )}

              <Divider />

              {/* ── Patient signature ───────────────────────────── */}
              {alreadySigned ? (
                <Alert
                  severity="success"
                  icon={<Iconify icon="eva:checkmark-circle-2-fill" />}
                >
                  <AlertTitle>Patient signature already held</AlertTitle>
                  Signed by{' '}
                  <strong>
                    {quote?.patientSignature.name || 'the patient'}
                  </strong>
                  {quote?.patientSignature.signedAt && (
                    <>
                      {' '}
                      on{' '}
                      {new Date(quote.patientSignature.signedAt).toLocaleString(
                        'en-GB',
                      )}
                    </>
                  )}{' '}
                  at dispense. It will be reused on this claim — the patient
                  does not need to sign again.
                </Alert>
              ) : (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No patient signature was captured when this was dispensed.
                    Capture it here and it is stored against the dispense, so
                    any resubmission or reprint reuses it.
                  </Alert>
                  <TextField
                    fullWidth
                    size="small"
                    label="Signed by (patient / guardian)"
                    value={signatoryName}
                    onChange={(e) => {
                      // NHS form: the signatory name is held in CAPITALS.
                      uppercaseElementValue(e.target);
                      setSignatoryName(e.target.value);
                    }}
                    sx={{ mb: 1 }}
                  />
                  <SignaturePad
                    label="Patient signature"
                    value={signature}
                    onChange={setSignature}
                    height={140}
                  />
                  <Box mt={1} display="flex" justifyContent="flex-end">
                    <LoadingButton
                      size="small"
                      variant="contained"
                      loading={savingSignature}
                      disabled={!signature || !signatoryName.trim()}
                      onClick={saveSignature}
                    >
                      Save signature
                    </LoadingButton>
                  </Box>
                </Box>
              )}

              <Divider />

              {/* ── Performer details ───────────────────────────── */}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    label="Optometrist GOC number"
                    value={gocNumber}
                    onChange={(e) => {
                      uppercaseElementValue(e.target);
                      setGocNumber(e.target.value);
                      setGocFromProfile(false);
                    }}
                    error={gocMissing}
                    helperText={
                      gocMissing
                        ? 'Required by PCSE. Record it under Settings → My Signature so it fills in automatically.'
                        : gocFromProfile
                          ? 'Filled in from your profile.'
                          : 'GOC registration number, required by PCSE on a GOS3.'
                    }
                  />
                </Grid>
              </Grid>

              <Alert severity="info">
                Your stored signature is attached automatically as the performer
                and supplier signature. Set it and your GOC number up under
                Settings → My Signature if you have not already.
              </Alert>

              <FormControlLabel
                control={
                  <Switch
                    checked={draft}
                    onChange={(e) => setDraft(e.target.checked)}
                  />
                }
                label="Save as a draft without submitting to PCSE"
              />
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button color="inherit" onClick={onClose}>
            {claim ? 'Done' : 'Cancel'}
          </Button>
          {!claim && (
            <LoadingButton
              variant="contained"
              loading={submitting}
              disabled={
                !canSubmit || (Boolean(existingClaim) && !allowDuplicate)
              }
              onClick={submit}
            >
              {draft ? 'Save draft claim' : 'Submit claim to PCSE'}
            </LoadingButton>
          )}
        </DialogActions>
      </Dialog>

      <GosClaimPrintDialog
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        claimId={claim?.id}
      />
    </>
  );
}
